import React, { useState, useEffect } from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import orderService from "../../../services/orderService";
import { toast } from "react-toastify";
import { handleImageError, getImageUrl } from "../../../utils/imageUtils";
import { Calendar as CalendarIcon } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const OrderTable = ({ onSelectOrder, searchQuery }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("All orders");
  const ordersPerPage = 10;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filter orders based on search, status, and date
  useEffect(() => {
    let filtered = orders;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((order) => {
        // Safely access nested properties
        const customerName = order.shippingAddress
          ? `${order.shippingAddress.firstName || ""} ${
              order.shippingAddress.lastName || ""
            }`.toLowerCase()
          : "";
        const phoneNumber = order.shippingAddress?.phoneNumber || "";
        const orderStatus = order.shippingStatus || "";
        const orderId = order._id || "";

        const searchTerm = searchQuery.toLowerCase();

        return (
          orderId.toLowerCase().includes(searchTerm) ||
          customerName.includes(searchTerm) ||
          phoneNumber.includes(searchTerm) ||
          orderStatus.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Apply status filter
    if (filter !== "All orders") {
      filtered = filtered.filter((order) => order.shippingStatus === filter);
    }

    // Apply date filter
    filtered = getFilteredOrdersByDate(filtered);

    // Update filtered orders
    setFilteredOrders(filtered.filter((order) => !order.isDeleted));
  }, [searchQuery, filter, dateFilter, selectedDate, orders]);

  const fetchOrders = async () => {
    try {
      const data = await orderService.getAllOrders();
      // Sort orders by createdAt date in descending order (latest first)
      const sortedOrders = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sortedOrders);
      setLoading(false);
    } catch (error) {
      toast.error(error.message || "Failed to fetch orders");
      setLoading(false);
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await orderService.updateOrderStatus(orderToDelete._id, {
        isDeleted: true,
      });
      toast.success("Order deleted successfully");
      setShowDeleteConfirm(false);
      setOrderToDelete(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.message || "Failed to delete order");
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setOrderToDelete(null);
  };

  const handleView = (order) => {
    onSelectOrder(order);
  };

  // Replace filter tabs with fulfillment status options
  const fulfillmentStatuses = [
    "All orders",
    "Processing",
    "Hold",
    "Packed",
    "Shipped",
    "Delivered",
  ];

  // Add new filter options
  const dateFilterOptions = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today's Orders" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "custom", label: "Custom Date" },
  ];

  // Filter orders based on date
  const getFilteredOrdersByDate = (orders) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    switch (dateFilter) {
      case "today":
        return orders.filter((order) => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= today;
        });
      case "week":
        return orders.filter((order) => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= weekStart;
        });
      case "month":
        return orders.filter((order) => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= monthStart;
        });
      case "custom":
        return selectedDate
          ? orders.filter((order) => {
              const orderDate = new Date(order.createdAt);
              orderDate.setHours(0, 0, 0, 0);
              return orderDate.getTime() === selectedDate.getTime();
            })
          : orders;
      default:
        return orders;
    }
  };

  // Calculate pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;

  // Update pagination calculation to use filtered orders
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const currentOrders = filteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  console.log(orders);

  return (
    <div className="w-full">
      {/* Filters Section */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Order Management Title */}
        <h2 className="text-xl font-semibold text-gray-800">
          Order Management
        </h2>
        <span className="text-gray-500 font-medium">
          ({filteredOrders.length} orders)
        </span>

        {/* Status Filter */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-48 p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        >
          {fulfillmentStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        {/* Date Filter */}
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="w-48 p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        >
          {dateFilterOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Calendar Picker */}
        {dateFilter === "custom" && (
          <div className="relative">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="MM/dd/yyyy"
              className="w-48 p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholderText="Select date"
            />
            <CalendarIcon
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              size={16}
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50">
            <tr className="border-b">
              <th className="px-6 py-4 text-gray-600 font-medium">
                Order Detail
              </th>
              <th className="px-6 py-4 text-gray-600 font-medium">
                Customer Detail
              </th>
              <th className="px-6 py-4 text-gray-600 font-medium">
                Payment Status
              </th>
              <th className="px-6 py-4 text-gray-600 font-medium">
                Fulfillment Status
              </th>
              <th className="px-6 py-4 text-gray-600 font-medium">
                Total Amount
              </th>
              <th className="px-6 py-4 text-gray-600 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => (
              <tr key={order._id} className="border-b hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      ORDER#{order._id.slice(-6)}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(order.createdAt).toLocaleString()}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span>{`${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`}</span>
                    <br />
                    <span className="text-gray-500">
                      {order.shippingAddress.phoneNumber}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={order.isPaid ? "text-green-500" : "text-red-500"}
                  >
                    {order.isPaid
                      ? `Paid $${Number(order.totalPrice).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )} on ${new Date(order.paidAt).toLocaleDateString()}`
                      : "Payment Pending"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`text-sm ${
                      order.shippingStatus === "Delivered"
                        ? "text-green-500"
                        : order.shippingStatus === "Shipped"
                        ? "text-blue-500"
                        : order.shippingStatus === "Packed"
                        ? "text-orange-500"
                        : order.shippingStatus === "Hold"
                        ? "text-red-500"
                        : "text-gray-500"
                    }`}
                  >
                    {order.shippingStatus || "Processing"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="font-medium">
                    $
                    {Number(order.totalPrice).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleView(order)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Eye className="w-4 h-4 text-blue-500" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(order)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="mb-6">Are you sure you want to delete this order?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 text-sm">
        <div>
          Showing {indexOfFirstOrder + 1} to{" "}
          {Math.min(indexOfLastOrder, filteredOrders.length)} of{" "}
          {filteredOrders.length} entries
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-purple-600 text-white"
                  : "border hover:bg-gray-50"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTable;
